import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorageIcon from '@mui/icons-material/Storage';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import ButtonComponent from '../components/Button';
import trainers from '../data/trainers';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const CourseCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
}));

const trainingStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Thrana Training Catalog',
  publisher: {
    '@type': 'Organization',
    name: 'Thranatech.com',
    url: 'https://www.thranatech.com',
  },
  itemListElement: [
    'Basic Module',
    'Advanced Module',
    'Pharmacovigilance',
    'Advanced Pharmacovigilance',
    'Clinical Data Management',
    'Regulatory Affairs',
    'Soft Skills',
  ].map((name, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name,
    url: 'https://www.thranatech.com/training',
  })),
};

const Training = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
    '/training-education.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const courses = [
    {
      id: 1,
      title: 'Basic Module',
      description: 'Comprehensive foundation course covering medical safety, clinical practices, drug development, and regulatory affairs fundamentals.',
      duration: '10 business days',
      level: 'Beginner',
      price: '₹25,000',
      icon: <ScienceIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'Introduction to Medical Safety',
        'Good Clinical Practices',
        'Fundamentals of Clinical research',
        'Drug Development life cycle',
        'Epidemological methods',
        'Clinical Data Management and Clinical Information Flow',
        'Clinical data management reports',
        'Guidelines to Pharmacovigilance- Module I',
        'Introduction to regulatory affair',
        'Quality and Complaince'
      ]
    },
    {
      id: 2,
      title: 'Advanced Module',
      description: 'Advanced training covering all basic topics plus public health ethics, biostatistics, regulations, and regulatory submissions.',
      duration: '15 business days',
      level: 'Intermediate',
      price: '₹35,000',
      icon: <TrendingUpIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'All basic topics plus',
        'Public health ethics and Policy',
        'Data Management and Biostatistics',
        'Regulations in clinical research',
        'Fundamentals of drug Safety',
        'Lab data and AE Management',
        'Importance of Medical writing in clinical research',
        'Guidelines to Pharmacovigilance- Module 2',
        'Regulatory Submissions',
        'Manufacturing and Supply chain priniciples'
      ]
    },
    {
      id: 3,
      title: 'Pharmacovigilance',
      description: 'Comprehensive pharmacovigilance training covering post-marketing surveillance, regulatory reporting, MedDRA, and adverse event processing.',
      duration: '45 business days',
      level: 'Advanced',
      price: '₹50,000',
      icon: <MedicationIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'Pharmacovigilance: Good Clinical Practice',
        'Pharmacovigilance: Overview of Post marketing Surveillance',
        'Pharmacovigilance: Regulatory Reporting Workshop',
        'Pharmacovigilance: Handling Safety Data for Marketed Products',
        'MedDRA: An Introduction',
        'Handling regulatory Inspections',
        'Narrative Workshop',
        'Adverse Event Report Processing Guideline',
        'Guidelines for Global Expectedness Assessment',
        'Handling Pregnancy Reports',
        'Seriousness Criteria: Important Medical Event',
        'Handling Product Complaints with Adverse Event Reports',
        'Detecting and Reporting Adverse Events from Literature & Case'
      ]
    },
    {
      id: 4,
      title: 'Advanced Pharmacovigilance',
      description: 'Customised training based on the Thrana Safety database covering case processing, case search, case inbox, and case entry.',
      duration: '15 days',
      level: 'Advanced',
      price: '₹35,000',
      icon: <StorageIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'Customised training based on the Thrana Safety database',
        'Case Processing in databases',
        'Case Search',
        'Case Inbox',
        'Case Entry'
      ]
    },
    {
      id: 5,
      title: 'Clinical Data Management',
      description: 'Complete training on clinical data management including protocol development, SOPs, data validation, query management, and database lock.',
      duration: '45 business days',
      level: 'Intermediate',
      price: '₹55,000',
      icon: <AssessmentIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'Introduction to Clinical data management',
        'Introduction to Clinical protocol',
        'SOP development',
        'Introduction to Clinical Trials',
        'Good clinical data management practices (GCDMP)',
        'Data Management Plan',
        'Clinical data management systems',
        'Clinical data repositories',
        'Query Management',
        'Data Clarification form',
        'Patient Dairy',
        'Clinical Data Entry',
        'Data validation',
        'SAE reconcillation',
        'e-CRF desining and data tracking from CRF',
        'Handing for different generated reports',
        'Database lock',
        'Clinical data Archiing process'
      ]
    },
    {
      id: 6,
      title: 'Regulatory Affairs',
      description: 'Comprehensive regulatory affairs training covering drug development lifecycle, IND/NDA applications, ICH guidelines, and global regulations.',
      duration: '45 business days',
      level: 'Advanced',
      price: '₹60,000',
      icon: <GavelIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'Drug development life cycle',
        'Understanding to drug regulations',
        'Historical perspective of drug regulations',
        'Good clinical Practices',
        'Regulatory affairs perspective in drug regulation',
        'Good manufacturing practices',
        'Drug approvals',
        'Investigational new drug (IND) application',
        'New Drug application (NDA)',
        'ICH history and evolution',
        'Common Technical document ICH',
        'Pharmaceutical quality /CMC',
        'INDs for Phase 2 and Phase 3 Studies',
        'Drug Master file',
        'Post Approval Regulatory affairs',
        'Post marketing adverse drug experience reporting experience',
        'Post approval maintenance of regulatory application/files',
        'Post approval changes',
        'General Labeling provisions',
        'Labeling: Prescription drugs',
        'Labeling: Over the counter products',
        'Drugs and Cosmetic Act',
        'Schedule Y',
        'Indian Regulatory affairs',
        'FDA regulatory environment and Guidance',
        'Regulations in Japan',
        'Regulations in Australia',
        'Regulations in Canada',
        'Regulatory process for Medical devices',
        'FDA- Biosimilars Guidance and Regulations',
        'EMEA- Biosimilars Guidance and Regulations',
        'Indian regulations-clinical trial and cosmetic biologicals',
        'Indian regulations-Pharmacovigilance requirements'
      ]
    },
    {
      id: 7,
      title: 'Soft Skills',
      description: 'Customized soft skills training for beginners and corporates, tailored to meet specific organizational requirements.',
      duration: 'Custom',
      level: 'All Levels',
      price: 'Based on requirement',
      icon: <PersonIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
      features: [
        'For Beginner & Corporates',
        'Customized training programs',
        'Tailored to organizational needs'
      ]
    }
  ];

  return (
    <>
      <SEO
        title="Thrana Training | Pharmacovigilance & Clinical Research Courses"
        description="Enroll in Thrana's instructor-led pharma training: basic and advanced pharmacovigilance modules, clinical data management, regulatory affairs, and soft skills."
        path="/training"
        structuredData={trainingStructuredData}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Pharma Training Services
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Professional Training Programs for Pharmacovigilance Excellence
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ color: '#3c005a', mb: 2 }}>
          Our Training Programs
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Choose from our range of specialized courses tailored for different skill levels
        </Typography>

        <Grid container spacing={4}>
          {courses.map((course) => (
            <Grid item xs={12} md={6} key={course.id}>
              <CourseCard elevation={3}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    {course.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom align="center" sx={{ color: '#3c005a' }}>
                    {course.title}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {course.duration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Level: {course.level}
                    </Typography>
                    {/* {course.price && (
                      <Typography variant="body2" sx={{ color: '#3c005a', fontWeight: 'bold' }}>
                        Price: {course.price}
                      </Typography>
                    )} */}
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {course.description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#3c005a', fontWeight: 'bold' }}>
                      Course Topics:
                    </Typography>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {course.features.map((feature, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">{feature}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <ButtonComponent
                    component={Link}
                    to={`/training/courses/${course.id}`}
                    size="large"
                  >
                    Enroll Now
                  </ButtonComponent>
                  <Button
                    component={Link}
                    to={`/training/courses/${course.id}`}
                    variant="outlined"
                    size="large"
                    sx={{ ml: 2, borderColor: '#3c005a', color: '#3c005a' }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </CourseCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#3c005a' }}>
            Why Choose Our Training Programs?
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Expert Instructors
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Learn from industry veterans with years of experience in pharmacovigilance and regulatory affairs.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Hands-on Experience
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Practical exercises and real-world case studies to enhance your understanding and skills.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Certification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Receive industry-recognized certificates upon successful completion of courses.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Flexible Learning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Online and offline options available to suit your schedule and learning preferences.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
          Ready to Start Your Learning Journey?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Contact us to learn more about our training programs or to schedule a consultation.
        </Typography>
        <Box sx={{ mt: 4 }}>
          <ButtonComponent
            component={Link}
            to="/contact"
            size="large"
          >
            Get in Touch
          </ButtonComponent>
        </Box>
      </Container>

      <Box sx={{ bgcolor: '#f8f5fb', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#3c005a' }}>
            Our Lead Instructors
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                Vasanthi S.E
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Senior Director – Pharmacovigilance & Medical Safety · Nearly 24 years of rich experience
              </Typography>
              <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                With nearly 24 years of rich experience in Medical Safety Projects, Delivery Management, client engagement, People Management, learning & development. Currently serving as Senior Director – Pharmacovigilance & Medical Safety at Thrana, Vasanthi spearheads end-to-end safety operations strategy, delivery oversight, partner engagement, and capability development to support Thrana's growing portfolio of innovative safety technology and service offerings.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                Sunil Pandita
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Chief Strategic & Scientific Officer · Nearly 20 years of experience in clinical research
              </Typography>
              <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                Sunil Pandita is a Quality Assurance and GCP auditor with nearly 20 years of experience in clinical research. He has led audits across Clinical operations, Data Management, Pharmacovigilance, BIOS, and Vendor process. With deep expertise in Global regulatory framework and compliance.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Instructors for: Basic Module, Advanced Module, Pharmacovigilance, Advanced Pharmacovigilance, Clinical Data Management, and Regulatory Affairs.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Meet Our Trainers section intentionally removed */}
      </Box>
    </>
  );
};

export default Training;

